import { useState, useCallback } from "react";
import type {
  RowSelectionState,
  OnChangeFn,
  Updater,
} from "@tanstack/react-table";

type SelectedRowsState = {
  selectedIdsArray: string[];
  selectedRowsObject: RowSelectionState; // Record<string, boolean>
};

export function useSelectedRows(initial?: Partial<SelectedRowsState>) {
  const [state, setState] = useState<SelectedRowsState>({
    selectedIdsArray: initial?.selectedIdsArray ?? [],
    selectedRowsObject: initial?.selectedRowsObject ?? {},
  });

  const setSelectedRows: OnChangeFn<RowSelectionState> = useCallback(
    (updaterOrValue: Updater<RowSelectionState>) => {
      setState((prev) => {
        const nextObject =
          typeof updaterOrValue === "function"
            ? (updaterOrValue as (old: RowSelectionState) => RowSelectionState)(
                prev.selectedRowsObject,
              )
            : updaterOrValue;

        const selectedIdsArray = Object.keys(nextObject).filter(
          (id) => nextObject[id],
        );

        return {
          selectedIdsArray,
          selectedRowsObject: nextObject,
        };
      });
    },
    [],
  );

  const removeSelectedRow = (id: string) => {
    setState((prev) => {
      const nextObject = { ...prev.selectedRowsObject };
      delete nextObject[id];
      return {
        selectedIdsArray: Object.keys(nextObject).filter(
          (id) => nextObject[id],
        ),
        selectedRowsObject: nextObject,
      };
    });
  };

  return [state, setSelectedRows, removeSelectedRow] as const;
}
